import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  SFPageContainer,
  SFAppBar,
  useSFAnimation,
  SFLoadingScreen,
  SFIcons,
  SFDialog,
  SFStack,
  SFTypography,
  SFLink,
  SFUserService,
  ToastContainer,
} from "@sf-core";
import HeaderLayout from "../../containers/headerLayout";
import {
  LPAssets,
  launchPadLayoutActions,
  profileActions,
  LPUrls,
  userConfigurationActions,
} from "@sf-launchpad";
import env from "env";
import { Logger, setAvatar } from "@base-provider";
import { ModalWrapper } from "./modalWrapper";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useUpdatesInterval from "../../hooks/useUpdatesInterval";
import { getLastestUpdates, useDoceboPlayer } from "@sf-launchpad";
import { useAuth0 } from "@auth0/auth0-react";
import NotificationLayout from "../../containers/notificationLayout";

const RoutesWrapper = ({
  children,
  routes,
  isSiteReady,
  isPageLoaderAnimationFinished,
  resetLoadingState,
  setLoadingState,
  state,
  showLoader,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dialog, setDialog] = useState({
    open: false,
    header: "",
    body: "",
    footer: "",
    actionBtns: [],
  });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const defaultInterval = env.REACT_APP_UPDATES_DEFAULT_INTERVAL_SECONDS;
  const onDemandInterval = env.REACT_APP_UPDATES_ON_DEMAND_INTERVAL_SECONDS;
  const onDemandDuration = env.REACT_APP_UPDATES_ON_DEMAND_DURATION_SECONDS;
  const updatesOnDemandInterval = useSelector(
    (state) => state.launchPadLayout.updatesOnDemandInterval
  );
  const userJobRoleId = useSelector(
    (state) => state.userConfiguration.jobRoleId
  );
  const lxpEnabledLearnerJobAndSkillVerification = useSelector(
    (state) =>
      state.session.tenantSettings.lxpEnabledLearnerJobAndSkillVerification
  );
  const showProfilePage =
    lxpEnabledLearnerJobAndSkillVerification &&
    location.pathname.includes(LPUrls.getProfileUrl());
  const showProgressPage =
    userJobRoleId && lxpEnabledLearnerJobAndSkillVerification;

  // Custom hook to change the updates interval dynamically from Default(30 seconds) to On Demand(1 second) when the user exits the course player
  // for the next minute or if user enters the course player again or a course completion is received while the countdown is running
  const updatesInterval = useUpdatesInterval(
    defaultInterval,
    onDemandInterval,
    onDemandDuration,
    updatesOnDemandInterval
  );

  //Get the latest updates for the courses, skillpaths and job domains from the server
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(getLastestUpdates());
    }, updatesInterval);
    return () => clearInterval(interval);
  }, [updatesInterval]);

  const HomeIconAnimation = useSFAnimation({
    animation: LPAssets.HomeIcon,
    loop: false,
    autoplay: false,
    style: { zoom: 0.2 },
  });

  const { user } = useAuth0();

  const email = user.email;

  useEffect(() => {
    resetLoadingState();
    // Get user details to get the user's avatar and name
    SFUserService.getUserDetails(email).then((res) => {
      if (res.avatar) dispatch(setAvatar(res.avatar));
      if (res.name)
        dispatch(
          userConfigurationActions.setState({
            name: res.name,
          })
        );
    });
  }, []);

  const doceboPlayerReady = useDoceboPlayer();
  Logger.of("App.RoutesWrapper.doceboPlayerReady").info(doceboPlayerReady);

  const getLeftMenuItems = (showProfilePage) => {
    if (showProfilePage) {
      return [
        {
          title: t("appBar.profileSetup.header"),
        },
      ];
    }
    return [
      {
        icon: HomeIconAnimation.View,
        title: t("appBar.home"),
        onClick: () => navigate(LPUrls.getHomeUrl()),
        onMouseOver: () => HomeIconAnimation.play(),
        onMouseOut: () => HomeIconAnimation.stop(),
      },
      ...(showProgressPage
        ? [
          {
            title: t("appBar.myProgress"),
            icon: <SFIcons.SFMyProgressIcon />,
            onClick: () => navigate(LPUrls.getMyProgressUrl()),
            path: LPUrls.getMyProgressUrl(),
          },
        ]
        : []),
    ];
  };

  const menuItems = getLeftMenuItems(showProfilePage);

  return (
    <>
      {doceboPlayerReady === false && <div>{t("LoadingPlayer")}</div>}
      <SFAppBar
        dividerLine={showProfilePage}
        disableGradient={showProfilePage || showLoader}
        logo={<LPAssets.SFLogo />}
        showUserAvatar={!showProfilePage}
        items={menuItems}
        itemsRight={
          showProfilePage
            ? [
              {
                icon: <SFIcons.SFCloseIcon />,
                title: t("appBar.profileSetup.exitButton"),
                onClick: () => {
                  // Save current user progress
                  dispatch(
                    profileActions.setState({
                      saveUserProfileProgress: true,
                    })
                  );
                },
              },
            ]
            : [
              {
                icon: <SFIcons.SFHelpIcon />,
                title: t("appBar.help"),
                onClick: () =>
                  setDialog({
                    open: true,
                    header: t("appBar.helpDialog.header"),
                    body: (
                      <SFStack spacing={3}>
                        <SFTypography variant="body2">
                          {t("appBar.helpDialog.body")}
                        </SFTypography>
                        <SFLink
                          href={`mailto:${env.REACT_APP_SUPPORT_EMAIL}`}
                        >
                          <SFStack direction="row" spacing={1}>
                            <SFIcons.SFEmailIcon />
                            <SFTypography variant="body2">
                              {env.REACT_APP_SUPPORT_EMAIL}
                            </SFTypography>
                          </SFStack>
                        </SFLink>
                      </SFStack>
                    ),
                  }),
              },
            ]
        }
      />
      <NotificationLayout />
      <SFPageContainer sx={{ mt: "80px", height: "calc(100vh - 80px)" }}>
        <ToastContainer />
        <ModalWrapper />
        <SFDialog
          size={"sm"}
          open={dialog.open}
          header={
            <SFTypography variant="academyCardTitle">
              {dialog.header}
            </SFTypography>
          }
          onClose={() => setDialog({ open: false })}
          actionBtns={dialog.actionBtns}
        >
          {dialog.body}
        </SFDialog>
        <HeaderLayout routes={routes} />
        {children}
      </SFPageContainer>
      {env.REACT_APP_ENABLE_PAGE_LOADER && (
        <SFLoadingScreen
          showLogo
          isVisible={!isSiteReady || !isPageLoaderAnimationFinished}
          onAnimationFinished={() => {
            setLoadingState({
              isPageLoaderAnimationFinished: true,
            });
          }}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  isSiteReady: state.launchPadLayout.isSiteReady,
  isPageLoaderAnimationFinished:
    state.launchPadLayout.isPageLoaderAnimationFinished,
  state,
  showLoader: state.launchPadLayout.showLoader,
});

const mapDispatchToProps = (dispatch) => {
  return {
    resetLoadingState: () => dispatch(launchPadLayoutActions.resetState()),
    setLoadingState: (o) => dispatch(launchPadLayoutActions.setState(o)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(RoutesWrapper);
